import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import Swal from "sweetalert2";
import { isEmpty } from "lodash";

import { DataContext } from "../../../../context/DataContext";
import { getToken } from "../../../../api/Account";
import { simpleMessage } from "../../../../helpers/Helpers";
import {
  addPlazoAsync,
  deletePlazoAsync,
  getPlazoByPeriodicidadAsync,
  updatePlazoAsync,
} from "../../../../api/Plazo";
import { monoFont, getTokens } from "../../../../theme";

// CRUD de plazos de una periodicidad. Se abre desde la fila correspondiente en
// PeriodicidadList, asi que la periodicidad ya viene seleccionada por props.
const PlazosManager = ({ periodicidad, onChanged }) => {
  const { setIsLoading, setIsUnautorized, isDarkMode } = useContext(DataContext);
  const t = getTokens(isDarkMode);
  const token = getToken();

  const [plazos, setPlazos] = useState([]);
  const [descripcion, setDescripcion] = useState("");
  const [cuotas, setCuotas] = useState("");
  const [editandoId, setEditandoId] = useState(null);

  const cargarPlazos = async () => {
    setIsLoading(true);
    const result = await getPlazoByPeriodicidadAsync(token, periodicidad.id);
    setIsLoading(false);

    if (!result.statusResponse) {
      if (result.error === "Unauthorized") {
        setIsUnautorized(true);
        return;
      }
      simpleMessage(result.error, "error");
      return;
    }
    setPlazos(result.data.result || []);
  };

  useEffect(() => {
    cargarPlazos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodicidad.id]);

  const limpiarFormulario = () => {
    setDescripcion("");
    setCuotas("");
    setEditandoId(null);
  };

  const validar = () => {
    if (!descripcion.trim()) {
      simpleMessage("Debe ingresar una descripción", "error");
      return false;
    }
    if (`${cuotas}`.trim() === "" || parseInt(cuotas, 10) <= 0) {
      simpleMessage("La cantidad de cuotas debe ser mayor a cero", "error");
      return false;
    }
    return true;
  };

  const guardar = async () => {
    if (!validar()) {
      return;
    }

    setIsLoading(true);
    const result = editandoId
      ? await updatePlazoAsync(token, {
          id: editandoId,
          description: descripcion.trim(),
          cuotas: parseInt(cuotas, 10),
        })
      : await addPlazoAsync(token, {
          description: descripcion.trim(),
          cuotas: parseInt(cuotas, 10),
          idPeriocidad: periodicidad.id,
        });
    setIsLoading(false);

    if (!result.statusResponse) {
      if (result.error === "Unauthorized") {
        setIsUnautorized(true);
        return;
      }
      simpleMessage(result.error, "error");
      return;
    }

    limpiarFormulario();
    await cargarPlazos();
    if (onChanged) {
      onChanged();
    }
  };

  const editar = (plazo) => {
    setEditandoId(plazo.id);
    setDescripcion(plazo.description || "");
    setCuotas(`${plazo.cuotas}`);
  };

  const eliminar = async (plazo) => {
    Swal.fire({
      title: "¿Estas Seguro?",
      text: `Se eliminará el plazo "${plazo.description}"`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#1d4ed8",
      cancelButtonColor: "#d83a3f",
      confirmButtonText: "Eliminar!",
      cancelButtonText: "Cancelar",
      customClass: { popup: "border" },
    }).then((res) => {
      if (res.isConfirmed) {
        (async () => {
          setIsLoading(true);
          const result = await deletePlazoAsync(token, plazo.id);
          setIsLoading(false);

          if (!result.statusResponse) {
            if (result.error === "Unauthorized") {
              setIsUnautorized(true);
              return;
            }
            simpleMessage(result.error, "error");
            return;
          }

          if (editandoId === plazo.id) {
            limpiarFormulario();
          }
          await cargarPlazos();
          if (onChanged) {
            onChanged();
          }
          Swal.fire("Eliminado!", "Plazo eliminado.", "success");
        })();
      }
    });
  };

  const accionSx = {
    width: 32,
    height: 32,
    borderRadius: "9px",
    border: `1px solid ${t.fieldLine}`,
    backgroundColor: t.field,
  };

  return (
    <Stack spacing={2.5}>
      <Box
        sx={{
          padding: "14px 16px",
          borderRadius: "12px",
          backgroundColor: t.field,
          border: `1px solid ${t.fieldLine}`,
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 600,
            fontSize: 13.5,
            color: "text.primary",
            marginBottom: "12px",
          }}
        >
          {editandoId ? "Editar plazo" : "Nuevo plazo"}
        </Typography>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="flex-start">
          <TextField
            size="small"
            fullWidth
            label="Descripción"
            placeholder="Ej. 12 quincenas"
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value.toUpperCase())}
          />
          <TextField
            size="small"
            label="Cuotas"
            type="number"
            value={cuotas}
            onChange={(e) => setCuotas(e.target.value)}
            sx={{ width: { xs: "100%", sm: 130 } }}
          />
        </Stack>

        <Stack direction="row" spacing={1.5} sx={{ marginTop: "14px" }}>
          <Button
            onClick={guardar}
            variant="contained"
            color="primary"
            startIcon={editandoId ? <SaveRoundedIcon /> : <AddRoundedIcon />}
          >
            {editandoId ? "Guardar cambios" : "Agregar plazo"}
          </Button>
          {editandoId ? (
            <Button onClick={limpiarFormulario} variant="outlined" startIcon={<CloseRoundedIcon />}>
              Cancelar
            </Button>
          ) : null}
        </Stack>
      </Box>

      <Box>
        <Typography sx={{ fontSize: 13, color: "text.disabled", marginBottom: "10px" }}>
          {isEmpty(plazos)
            ? "Esta periodicidad todavía no tiene plazos."
            : `${plazos.length} ${plazos.length === 1 ? "plazo" : "plazos"} registrados`}
        </Typography>

        <Stack spacing={1}>
          {plazos.map((plazo) => (
            <Stack
              key={plazo.id}
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{
                padding: "10px 14px",
                borderRadius: "11px",
                border: "1px solid",
                borderColor: editandoId === plazo.id ? "primary.main" : "divider",
                backgroundColor: "background.paper",
              }}
            >
              <Box
                component="span"
                sx={{
                  fontFamily: monoFont,
                  fontWeight: 700,
                  fontSize: 14,
                  color: "primary.main",
                  minWidth: 34,
                }}
              >
                {plazo.cuotas}
              </Box>
              <Typography sx={{ flex: 1, fontSize: 13.5, fontWeight: 700, color: "text.primary" }}>
                {plazo.description}
              </Typography>

              <Tooltip title="Editar">
                <IconButton
                  size="small"
                  sx={{ ...accionSx, color: "warning.main" }}
                  onClick={() => editar(plazo)}
                >
                  <EditRoundedIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Eliminar">
                <IconButton
                  size="small"
                  sx={{ ...accionSx, color: "error.main" }}
                  onClick={() => eliminar(plazo)}
                >
                  <DeleteRoundedIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          ))}
        </Stack>
      </Box>
    </Stack>
  );
};

export default PlazosManager;
